import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, Plus, Search, Filter, MessageSquare, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [idsDialogOpen, setIdsDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [idsStep, setIdsStep] = useState('identify'); // identify, discuss, solve

  // Form state
  const [issueForm, setIssueForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    owner_id: '',
    department: ''
  });

  // IDS Process state
  const [idsData, setIdsData] = useState({
    identify: {
      root_cause: '',
      identified_at: null,
      identified_by: ''
    },
    discuss: {
      discussion_notes: '',
      discussion_duration_minutes: 0,
      participants: [],
      ai_suggestions: [],
      discussed_at: null
    },
    solve: {
      solution: '',
      action_items: [],
      rocks_created: [],
      solved_at: null,
      solved_by: ''
    }
  });

  useEffect(() => {
    fetchIssues();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [searchQuery, filterPriority, filterStatus]);

  const fetchIssues = async () => {
    try {
      const params = {};
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await axios.get(`${API}/eos/issues`, { params });
      let filteredIssues = response.data;

      if (searchQuery) {
        filteredIssues = filteredIssues.filter(issue =>
          issue.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          issue.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setIssues(filteredIssues);
    } catch (error) {
      console.error('Error fetching issues:', error);
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateIssue = async () => {
    try {
      if (!issueForm.title || !issueForm.owner_id) {
        toast.error('Please fill in title and owner');
        return;
      }

      await axios.post(`${API}/eos/issues`, issueForm);
      toast.success('Issue created successfully');
      setCreateDialogOpen(false);
      resetIssueForm();
      fetchIssues();
    } catch (error) {
      console.error('Error creating issue:', error);
      toast.error(error.response?.data?.detail || 'Failed to create issue');
    }
  };

  const handleUpdateIssue = async (issueId, updates) => {
    try {
      await axios.put(`${API}/eos/issues/${issueId}`, updates);
      toast.success('Issue updated successfully');
      fetchIssues();
    } catch (error) {
      console.error('Error updating issue:', error);
      toast.error('Failed to update issue');
    }
  };

  const openIdsDialog = (issue) => {
    setSelectedIssue(issue);
    
    // Load existing IDS data if available
    if (issue.ids_process) {
      setIdsData(issue.ids_process);
      
      // Determine current step based on what's completed
      if (issue.status === 'solved') {
        setIdsStep('solve');
      } else if (issue.status === 'in_discussion') {
        setIdsStep('discuss');
      } else {
        setIdsStep('identify');
      }
    } else {
      // Initialize empty IDS data
      setIdsData({
        identify: { root_cause: '', identified_at: null, identified_by: '' },
        discuss: { discussion_notes: '', discussion_duration_minutes: 0, participants: [], ai_suggestions: [], discussed_at: null },
        solve: { solution: '', action_items: [], rocks_created: [], solved_at: null, solved_by: '' }
      });
      setIdsStep('identify');
    }
    
    setIdsDialogOpen(true);
  };

  const handleStartIDS = async () => {
    if (!selectedIssue) return;

    try {
      // Update issue status to in_discussion
      await handleUpdateIssue(selectedIssue.id, { status: 'in_discussion' });
      setIdsStep('discuss');
    } catch (error) {
      console.error('Error starting IDS:', error);
    }
  };

  const handleSolveIssue = async () => {
    if (!selectedIssue || !idsData.solve.solution) {
      toast.error('Please document the solution');
      return;
    }

    try {
      // Update issue status to solved
      await handleUpdateIssue(selectedIssue.id, { 
        status: 'solved'
      });
      
      toast.success('Issue solved successfully');
      setIdsDialogOpen(false);
      setSelectedIssue(null);
      fetchIssues();
    } catch (error) {
      console.error('Error solving issue:', error);
      toast.error('Failed to solve issue');
    }
  };

  const resetIssueForm = () => {
    setIssueForm({
      title: '',
      description: '',
      priority: 'medium',
      owner_id: '',
      department: ''
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return '🔴 High';
      case 'medium': return '🟡 Medium';
      case 'low': return '🟢 Low';
      default: return 'Unknown';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'identified': return '📝 Identified';
      case 'in_discussion': return '🔍 In Discussion';
      case 'solved': return '✅ Solved';
      case 'archived': return '🗄️ Archived';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'identified': return 'border-orange-500 bg-orange-50 dark:bg-orange-950';
      case 'in_discussion': return 'border-blue-500 bg-blue-50 dark:bg-blue-950';
      case 'solved': return 'border-green-500 bg-green-50 dark:bg-green-950';
      case 'archived': return 'border-gray-500 bg-gray-50 dark:bg-gray-950';
      default: return 'border-gray-300';
    }
  };

  const calculateAge = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Group issues by priority
  const highPriorityIssues = issues.filter(i => i.priority === 'high' && i.status !== 'solved');
  const mediumPriorityIssues = issues.filter(i => i.priority === 'medium' && i.status !== 'solved');
  const lowPriorityIssues = issues.filter(i => i.priority === 'low' && i.status !== 'solved');
  const solvedIssues = issues.filter(i => i.status === 'solved');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Issues...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div>
              <h1 className="text-3xl font-bold">Issues List (IDS)</h1>
              <p className="text-sm text-muted-foreground">
                Identify, Discuss, and Solve problems systematically
              </p>
            </div>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Issue
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{issues.length}</div>
                <div className="text-sm text-muted-foreground">Total Issues</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{highPriorityIssues.length}</div>
                <div className="text-sm text-muted-foreground">High Priority</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {issues.filter(i => i.status === 'in_discussion').length}
                </div>
                <div className="text-sm text-muted-foreground">In Discussion</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{solvedIssues.length}</div>
                <div className="text-sm text-muted-foreground">Solved</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="identified">Identified</SelectItem>
              <SelectItem value="in_discussion">In Discussion</SelectItem>
              <SelectItem value="solved">Solved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* High Priority Issues */}
        {highPriorityIssues.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🔴</span>
                <div>
                  <CardTitle>High Priority</CardTitle>
                  <CardDescription>{highPriorityIssues.length} urgent issues</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {highPriorityIssues.map(issue => (
                  <Card 
                    key={issue.id} 
                    className={`cursor-pointer hover:shadow-md transition-shadow ${getStatusColor(issue.status)}`}
                    onClick={() => openIdsDialog(issue)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{issue.title}</h3>
                            <Badge variant="outline">{getStatusLabel(issue.status)}</Badge>
                          </div>
                          {issue.description && (
                            <p className="text-sm text-muted-foreground mb-2">{issue.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>
                              Owner: {issue.owner_details 
                                ? `${issue.owner_details.first_name} ${issue.owner_details.last_name}`
                                : 'N/A'}
                            </span>
                            <span>•</span>
                            <span>{calculateAge(issue.created_at)} days old</span>
                            {issue.department && (
                              <>
                                <span>•</span>
                                <span>{issue.department}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openIdsDialog(issue);
                          }}
                        >
                          Start IDS
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Medium Priority Issues */}
        {mediumPriorityIssues.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🟡</span>
                <div>
                  <CardTitle>Medium Priority</CardTitle>
                  <CardDescription>{mediumPriorityIssues.length} issues</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {mediumPriorityIssues.map(issue => (
                  <Card 
                    key={issue.id} 
                    className={`cursor-pointer hover:shadow-md transition-shadow ${getStatusColor(issue.status)}`}
                    onClick={() => openIdsDialog(issue)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{issue.title}</h3>
                            <Badge variant="outline">{getStatusLabel(issue.status)}</Badge>
                          </div>
                          {issue.description && (
                            <p className="text-sm text-muted-foreground mb-2">{issue.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>
                              Owner: {issue.owner_details 
                                ? `${issue.owner_details.first_name} ${issue.owner_details.last_name}`
                                : 'N/A'}
                            </span>
                            <span>•</span>
                            <span>{calculateAge(issue.created_at)} days old</span>
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openIdsDialog(issue);
                          }}
                        >
                          {issue.status === 'identified' ? 'Start IDS' : 'Continue IDS'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low Priority Issues */}
        {lowPriorityIssues.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🟢</span>
                <div>
                  <CardTitle>Low Priority</CardTitle>
                  <CardDescription>{lowPriorityIssues.length} issues</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {lowPriorityIssues.map(issue => (
                  <Card 
                    key={issue.id} 
                    className={`cursor-pointer hover:shadow-md transition-shadow ${getStatusColor(issue.status)}`}
                    onClick={() => openIdsDialog(issue)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{issue.title}</h3>
                            <Badge variant="outline">{getStatusLabel(issue.status)}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>
                              Owner: {issue.owner_details 
                                ? `${issue.owner_details.first_name} ${issue.owner_details.last_name}`
                                : 'N/A'}
                            </span>
                            <span>•</span>
                            <span>{calculateAge(issue.created_at)} days old</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Solved Issues */}
        {solvedIssues.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle>Solved Issues</CardTitle>
                  <CardDescription>{solvedIssues.length} completed</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {solvedIssues.slice(0, 5).map(issue => (
                  <div 
                    key={issue.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => openIdsDialog(issue)}
                  >
                    <div>
                      <div className="font-medium">{issue.title}</div>
                      <div className="text-xs text-muted-foreground">
                        Solved {calculateAge(issue.updated_at)} days ago
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50 dark:bg-green-950">
                      ✅ Solved
                    </Badge>
                  </div>
                ))}
                {solvedIssues.length > 5 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{solvedIssues.length - 5} more solved issues
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {issues.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No issues yet. Add your first issue to get started!</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Issue
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Issue Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Issue</DialogTitle>
              <DialogDescription>Add a problem or concern to be solved</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Issue Title *</Label>
                <Input
                  placeholder="e.g., Sales pipeline conversion dropping"
                  value={issueForm.title}
                  onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Provide more details about the issue..."
                  value={issueForm.description}
                  onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority *</Label>
                  <Select 
                    value={issueForm.priority}
                    onValueChange={(value) => setIssueForm({ ...issueForm, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">🔴 High Priority</SelectItem>
                      <SelectItem value="medium">🟡 Medium Priority</SelectItem>
                      <SelectItem value="low">🟢 Low Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Owner *</Label>
                  <Select 
                    value={issueForm.owner_id}
                    onValueChange={(value) => setIssueForm({ ...issueForm, owner_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Department (Optional)</Label>
                <Select 
                  value={issueForm.department}
                  onValueChange={(value) => setIssueForm({ ...issueForm, department: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                resetIssueForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateIssue}>Create Issue</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* IDS Process Dialog */}
        <Dialog open={idsDialogOpen} onOpenChange={setIdsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>IDS Process: {selectedIssue?.title}</DialogTitle>
              <DialogDescription>
                {getPriorityLabel(selectedIssue?.priority)} | {getStatusLabel(selectedIssue?.status)}
              </DialogDescription>
            </DialogHeader>

            {/* IDS Steps */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <Button
                variant={idsStep === 'identify' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIdsStep('identify')}
              >
                1. Identify
              </Button>
              <div className="h-px w-8 bg-border"></div>
              <Button
                variant={idsStep === 'discuss' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIdsStep('discuss')}
                disabled={selectedIssue?.status === 'identified'}
              >
                2. Discuss
              </Button>
              <div className="h-px w-8 bg-border"></div>
              <Button
                variant={idsStep === 'solve' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIdsStep('solve')}
                disabled={selectedIssue?.status === 'identified'}
              >
                3. Solve
              </Button>
            </div>

            {/* Step Content */}
            {idsStep === 'identify' && (
              <div className="space-y-4">
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-semibold text-lg mb-2">Identify the Root Cause</h3>
                  <p className="text-sm text-muted-foreground">
                    What&apos;s the REAL issue? Not the symptom, but the underlying problem.
                  </p>
                </div>

                <div>
                  <Label>Issue Description</Label>
                  <p className="text-sm p-3 bg-muted rounded-lg">
                    {selectedIssue?.description || 'No description provided'}
                  </p>
                </div>

                <div>
                  <Label>Root Cause Analysis</Label>
                  <Textarea
                    placeholder="Document the root cause of this issue..."
                    value={idsData.identify.root_cause}
                    onChange={(e) => setIdsData({
                      ...idsData,
                      identify: { ...idsData.identify, root_cause: e.target.value }
                    })}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ask &quot;Why?&quot; 5 times to get to the root cause
                  </p>
                </div>

                <Button onClick={handleStartIDS} className="w-full">
                  Next: Discuss →
                </Button>
              </div>
            )}

            {idsStep === 'discuss' && (
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-lg mb-2">Discuss Solutions</h3>
                  <p className="text-sm text-muted-foreground">
                    Collaborate with the team to explore potential solutions.
                  </p>
                </div>

                <div>
                  <Label>Discussion Notes</Label>
                  <Textarea
                    placeholder="Document the discussion, ideas, and approaches explored..."
                    value={idsData.discuss.discussion_notes}
                    onChange={(e) => setIdsData({
                      ...idsData,
                      discuss: { ...idsData.discuss, discussion_notes: e.target.value }
                    })}
                    rows={6}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Keep discussion focused on solutions, not blame
                  </p>
                </div>

                <Button onClick={() => setIdsStep('solve')} className="w-full">
                  Next: Solve →
                </Button>
              </div>
            )}

            {idsStep === 'solve' && (
              <div className="space-y-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-lg mb-2">Document the Solution</h3>
                  <p className="text-sm text-muted-foreground">
                    What&apos;s the agreed-upon solution? What actions will be taken?
                  </p>
                </div>

                <div>
                  <Label>Solution *</Label>
                  <Textarea
                    placeholder="Document the solution and action plan..."
                    value={idsData.solve.solution}
                    onChange={(e) => setIdsData({
                      ...idsData,
                      solve: { ...idsData.solve, solution: e.target.value }
                    })}
                    rows={5}
                  />
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm font-medium mb-2">💡 Quick Action:</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Create a To-Do directly from this issue to track action items.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      toast.info('To-Do creation feature available in To-Dos page');
                      setIdsDialogOpen(false);
                      setTimeout(() => {
                        window.location.href = '/eos/todos';
                      }, 1000);
                    }}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create To-Do from Issue
                  </Button>
                </div>

                <Button onClick={handleSolveIssue} className="w-full">
                  Mark as Solved ✓
                </Button>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIdsDialogOpen(false);
                setSelectedIssue(null);
                setIdsStep('identify');
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EOSIssues;
